<template>
  <div class="def-phone">
    <span class="def-phone__label">{{ placeholder }}</span>
    <input
      class="def-phone-hidden"
      type="text"
      :name="name"
      :value="savedValue"
      :required="required"
      @invalid.prevent="handlerInvalid"
    />
    <!--стоп для change не можна робити-->
    <input
      ref="input"
      class="def-intl-input"
      :class="{ 'def-intl-input_invalid': !isValid || isShowError }"
      type="tel"
      :value="inputedPhone"
      :readonly="readonly"
      @countrychange="countrychange"
      @input="handlerInput"
      @paste="handlerSaveValue"
      @change="handlerChange"
    />
    <span v-if="(!isValid || isShowError) && errorText" class="def-phone-error">
      {{ errorText }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import intlTelInput from 'intl-tel-input';
import type { SomeOptions, Iti, Iso2 } from 'intl-tel-input';
import { useResultsStore } from '@/storePinia/stores/results';
import useLoader from '@use/useLoader/useLoader';

import { computed, onMounted, ref, onBeforeUnmount } from 'vue';

const resultsStore = useResultsStore();
const loader = useLoader();

const props = withDefaults(
  defineProps<{
    onlyCountries?: string[];
    initialCountry?: string;
    name?: string;
    value?: string;
    placeholder?: string;
    required?: boolean;
    isShowError?: boolean;
    readonly?: boolean;
    errorText?: string;
    removePlus?: boolean;
  }>(),
  {
    removePlus: true,
  }
);

const input = ref<HTMLInputElement | null>(null);
const phoneIsoCode = ref<string>('');
const countries = computed(() => resultsStore.deliveryCountries?.items);

let iti: Iti | null = null;
const isValid = ref(true);

defineExpose({
  phoneIsoCode,
});

const formatSavedPhone = (phone: string | undefined) => {
  if (!phone) return '';

  return props.removePlus ? phone.replace(/[+ ]/g, '') : phone;
};

const savedValue = ref<string>(formatSavedPhone(props.value));

const emit = defineEmits([
  'countrychange',
  'changePhone',
  'inputPhone',
  'completedPhone',
  'update:modelValue',
]);

const handlerSaveValue = () => {
  if (iti?.isValidNumberPrecise()) {
    savedValue.value = formatSavedPhone(iti?.getNumber());
    isValid.value = true;
    updateIsoCode();

    emit('completedPhone', savedValue.value);
  } else {
    savedValue.value = '';
    if (iti?.getNumber()?.length) {
      isValid.value = false;
    }
  }

  emit('inputPhone', iti?.getNumber());
  emit('update:modelValue', savedValue.value);
};

const inputedPhone = ref(props.value);

const handlerInput = (e) => {
  // якщо країна не обрана блокуємо ввод телефону
  if (!iti?.getSelectedCountry()?.iso2) {
    (e.target as HTMLInputElement).value = '';
    e.preventDefault();
    return;
  }

  handlerSaveValue();
  inputedPhone.value = e.target.value;
};

const handlerChange = () => {
  emit('changePhone', iti?.getNumber());
};

const countrychange = () => {
  handlerSaveValue();

  emit('countrychange', iti?.getSelectedCountry()?.iso2);
};

const handlerInvalid = () => {
  isValid.value = false;
};
const updateIsoCode = () => {
  phoneIsoCode.value = iti?.getSelectedCountry()?.iso2;
};

onMounted(() => {
  try {
    if (input.value) {
      const countriesCode = countries.value.map((el) => el.UF_COUNTRY_ISO_CODE);
      // реально дозволені країни: обмеження з пропса має пріоритет над країнами доставки
      const allowedCountries = (
        props.onlyCountries?.length ? props.onlyCountries : countriesCode
      ).map((el) => el.toUpperCase());
      // якщо дозволена лише одна країна, ставимо її одразу (селектор вимкнений, обрати іншу не можна)
      const singleCountry = allowedCountries.length === 1 ? allowedCountries[0] : '';

      const options: SomeOptions = {
        loadUtils: () => import(/* webpackChunkName: 'intl-tel-utils' */ 'intl-tel-input/utils'),
        strictMode: true,
        onlyCountries: countriesCode as Iso2[],
        initialCountry: (props.initialCountry || singleCountry.toLowerCase()) as Iso2,
        placeholderNumberType: 'MOBILE',
        allowedNumberTypes: ['MOBILE'],
        // у v25+ змінились дефолти: повертаємо поведінку v24 (код країни в полі, національний формат, без анімації відхилення вводу)
        separateDialCode: false,
        numberDisplayFormat: 'NATIONAL',
        strictRejectAnimation: false,
      };

      if (props.onlyCountries) {
        options.onlyCountries = props.onlyCountries as Iso2[];
      }

      if (props.onlyCountries?.length === 1 || props.readonly) {
        options.countrySelectorMode = 'OFF';
      }

      const isAutoCountry = !props.initialCountry && !props.value;

      iti = intlTelInput(input.value, options);
      if (isAutoCountry && singleCountry) {
        emit('countrychange', singleCountry);
      }
      // якщо країна не передається робимо запит в ручну на отримання країни.
      if (isAutoCountry && !singleCountry) {
        loader.start();
        fetch('https://ipapi.co/json')
          .then((res) => res.json())
          .then((data) => {
            if (allowedCountries.includes(data.country_code) || !allowedCountries.length) {
              iti?.setSelectedCountry(data.country_code.toLowerCase());
              emit('countrychange', data.country_code);
            } else {
              // iti?.setSelectedCountry('ua');
              // emit('countrychange', 'ua');
            }
          })
          .catch(() => {
            // iti.setSelectedCountry('ua');
            // emit('countrychange', 'ua');
          })
          .finally(() => loader.stop());
      }

      updateIsoCode();
    }
  } catch (err) {
    console.error(err);
  }
});

onBeforeUnmount(() => {
  if (iti) {
    iti.destroy();
    iti = null;
  }
});
</script>

<style lang="scss" scoped>
.def-phone {
  position: relative;
  &__label {
    position: absolute;
    top: 8px;
    left: 100px;
    color: var(--color-dark-grey);
    transition: 0.2s linear top;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: calc(100% - 132px);
    overflow: hidden;
    font-size: 14px;
    font-weight: 500;
    z-index: 1;
  }
  :deep(.iti) {
    width: 100%;
  }
  .def-intl-input {
    height: 52px;
    border: 1px solid #c8c8c8;
    width: 100%;
    padding-left: 100px !important;
    &_invalid {
      border-color: var(--color-red) !important;
    }
  }
  :deep(input.def-intl-input) {
    padding-left: 100px;
    padding-top: 15px;
  }
  :deep(.iti__arrow) {
    border: none;
    background: url('data:image/svg+xml;utf8,<svg  viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M4.4328 7.78295C4.75987 7.45588 5.29016 7.45588 5.61723 7.78295L10.0501 12.2158L14.483 7.78295C14.8101 7.45588 15.3403 7.45588 15.6674 7.78295C15.9945 8.11002 15.9945 8.6403 15.6674 8.96737L10.6423 13.9925C10.3153 14.3195 9.78497 14.3195 9.4579 13.9925L4.4328 8.96737C4.10573 8.6403 4.10573 8.11002 4.4328 7.78295Z"/> </svg> ');
    width: 20px;
    height: 20px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transition: 0.2s linear transform;
    margin-left: 8px;
    margin-top: 0;
    // v25+ малює стрілку рамкою з поворотом на 45°, скидаємо поворот для нашої svg
    transform: none;
  }
  :deep(.iti__arrow--up) {
    transform: rotate(-180deg);
  }
  :deep(.iti__selected-country-primary) {
    border-right: 1px solid #c8c8c8;
    width: 84px;
    padding: 0 16px;
  }
  :deep(.iti__country-container) {
    left: 0;
    right: unset !important;
  }
}

.def-phone-hidden {
  display: none;
  &:valid + div .def-intl-input {
    border-color: var(--color-green);
  }
}
.def-phone-error {
  display: inline-block;
  margin-left: 16px;
  font-size: 14px;
  font-weight: 500;
  line-height: 125%;
  margin-top: 5px;
  color: var(--color-red);
}
:global(.iti--fullscreen-popup) {
  z-index: 10003;
}
:global(.iti__search-input) {
  height: 36px;
  min-height: 36px;
  padding: 0 16px;
}
</style>
